import CreditApplicationInterface from "@/types_and_interfaces/credit_application_interface.ts";
import {ApplicationStatus} from "@/types_and_interfaces/types.ts";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useAtom} from "jotai/index";
import {loan_sidebar_data, profile} from "@/atoms/profile.ts";
import useLoans from "@/hooks/loans.ts";
import {contract} from "@/atoms/common.ts";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import CommonClass from "@/utils/common.ts";
import clsx from "clsx";
import st from "@/styles/PendingLoans.module.scss";
import Button from "@/ui/Button.tsx";
import DashboardClass from "@/utils/dashboard_class.ts";
import {CircularProgress} from "@mui/joy";
import useCommon from "@/hooks/common.ts";
import {DownloadIcon} from "lucide-react";


interface ApplicationProps {
    application: CreditApplicationInterface
    simplified_status: ApplicationStatus
    order?:number
}

const Application:React.FC<ApplicationProps> = ({application, simplified_status, order}) => {
    const {t} = useTranslation()
    const common = useCommon()
    const navigate = useNavigate()
    const [, set_data] = useAtom(loan_sidebar_data)
    const [user] = useAtom(profile)
    const loans = useLoans()
    const effect_ran = useRef<boolean>(false)
    const [contract_atom] = useAtom(contract)
    const is_contract_inside =  contract_atom.hasOwnProperty(application.urn);
    const [contract_enable, set_contract_enable] = useState<boolean | undefined>(undefined)
    const [contract_error, set_contract_error] = useState<boolean>(false)
    const interval = useRef<any>(null)
    // const common_hook = useCommon()
    const verified =  user?.kyc_general_status === "ProcessingSuccess" || user?.auth_type === "ByMPass"
    const data_list = [
        {
            value: `${CommonClass.formatMoneyAmount(Number(application.amount))} ${t("currency")}`,
            label: `${t("amount")}`,
            visible: true
        },
        {
            value: `${application.nr_of_installments} ${t(CommonClass.time_word_handling(application.nr_of_installments, true))}`,
            label: `${t("period")} `,
            visible: true
        },
        {
            value: `${CommonClass.formatMoneyAmount(Number(application.installment_amount))} ${t("currency")}`,
            label: t("monthly_payment"),
            visible: application.product_name !== "refinance"
        },
    ]

    useEffect(() => {

        if(application.status === "approved_and_need_sign" && verified && application.product_name !== "auto" && !!application?.ready_for_sign
            && application.is_enable_to_sign && application.is_contract_downloadable) {

            const upd = async () => {
                let is_contract = await loans.prefetch_contract({
                    id: application?.id,
                    uid: application?.urn
                }, application?.urn)

                if(!is_contract) {
                    
                    let count = 0

                    const interval = setInterval(async () => {
                        
                        count+=1
                        
                        is_contract = await loans.prefetch_contract({
                            id: application?.id,
                            uid: application?.urn
                        }, application?.urn)
                        
                        set_contract_enable(is_contract)
                        
                        if(is_contract || count > 10) {
                            clearInterval(interval)
                        }
                        
                        if(count > 4){
                            set_contract_error(true)
                        }
                        
                    }, 10000)
                    
                } else {
                    set_contract_enable(is_contract)
                }
                
                
            }
            
            if(!effect_ran.current) {
                const t = setTimeout(() => {
                    upd()
                    clearTimeout(t)
                }, (order ? order + 1 : 1) * 500)
            }
            
            effect_ran.current = true
            
        }
        
        return () => {
            clearInterval(interval.current)
        }
        
    }, [simplified_status, user]);

    const showSignButton= application.is_contract_downloadable &&
        (!user?.identity_card_is_expired || !user?.has_asp_kyc_conflict)

    return (
        <>
            <div
                className={clsx(
                    st.application,
                    st[simplified_status]
                )}
            >
                
                <span className={st.name}>
                    {t(application.product_name)}
                </span>
                
                <div className={st.header}>
                    {application.urn.length ? (
                        
                        <div className={st.main_infs}>
                            <strong onClick={() => {
                                common.copyToClipboard(application.urn)
                            }}>
                                {t("appl_nr")} <u>{application.urn}</u>
                            </strong>
                        </div>
                    
                    ) : null}
                    
                    <div className={st.status}>
                        
                        {(() => {
                            if (simplified_status === "sign_with_kyc" && user?.kyc_general_status === "ProcessingSuccess") {
                                return t("processing")
                            } else if (simplified_status === "approved" && user?.kyc_general_status === "ProcessingSuccess") {
                                return  t("approved_and_payment_pending")
                            } else {
                                return t(simplified_status)
                            }
                            
                        })()}
                    
                    </div>
                </div>
                
                <div className={st.data}>
                    {data_list.map((d: any, i) => {
                        
                        return d.visible ? (
                            <div key={`data_item_${application.id}_${i}`} className={st.data_item}>
                                <span>
                                    {d.label}
                                </span>
                                
                                <strong>
                                    {d.value}
                                </strong>
                            </div>
                        ) : null
                    })}
                </div>
                {/*TODO : go to sign*/}

                {application.product_name !== "auto" ? (
                    <>
                        <div className={st.sign_controllers}>
                            {(application.status === "approved_and_not_verified" || application.status === "approved_and_need_sign") && (
                                <>
                                    {!application.is_enable_to_sign ? (
                                        <p className={st.danger}>{t('is_enabled_to_sign_error')}</p>
                                    ) : showSignButton ? (
                                        <Button
                                            disabled={
                                                (application.status === "approved_and_need_sign" && verified && !is_contract_inside) ||
                                                (application.status === "approved_and_not_verified" && !user?.permissions.is_kyc_allowed) ||
                                                (application.status === "approved_and_need_sign" && !verified && !user?.permissions.is_kyc_allowed) ||
                                                (application.status === "approved_and_not_verified" && user?.kyc_general_status === "ProcessingFailedAndSentToManager") ||
                                                (application.status === "approved_and_need_sign" && user?.kyc_general_status === "ProcessingFailedAndSentToManager")
                                            }
                                            className={st.sign_contract}
                                            onClick={() => {
                                                if ((application.status === "approved_and_need_sign" && !verified) || application.status === "approved_and_not_verified") {

                                                    if (user?.kyc_general_status === "ProcessingSuccess") {
                                                        eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "info", t("data_is_processing"))
                                                        return
                                                    }

                                                    navigate(DashboardClass.dashboard_routes.KYC)


                                                } else {

                                                    set_data(JSON.stringify({
                                                        amount: application.amount,
                                                        end: CommonClass.date_plus_months(application?.nr_of_installments) || "",
                                                        period: application?.nr_of_installments
                                                    }))

                                                    navigate(DashboardClass.dashboard_routes.SignContract + "/" + application.urn)
                                                }
                                            }}
                                        >

                                            {!verified ? t("got_to_kyc") : (
                                                <>
                                                    {(!is_contract_inside && !contract_error) &&
                                                        <CircularProgress color={"neutral"} size={"sm"}/>}
                                                    {t("sign_contract")}
                                                </>
                                            )}

                                        </Button>
                                    )
                                    : null}

                                    {((application.status === "approved_and_not_verified" && user?.kyc_general_status === "ProcessingFailedAndSentToManager") ||
                                        (application.status === "approved_and_need_sign" && user?.kyc_general_status === "ProcessingFailedAndSentToManager")) ? (
                                        <span className={st.danger}>
                                            {t("failed_kyc1")}
                                        </span>
                                    ) : null}
                                </>
                            )}
                            
                            {((simplified_status === "pending_needs_kyc" || application.status === "approved" || application.status === "approved_and_checked") && user?.kyc_general_status !== "ProcessingSuccess") && (
                                <>
                                    <Button
                                        disabled={!user?.permissions.is_kyc_allowed}
                                        className={st.sign_contract}
                                        onClick={() => {
                                            navigate(DashboardClass.dashboard_routes.KYC)
                                        }}
                                    >
                                        {t("got_to_kyc")}
                                    </Button>
                                    
                                    {user?.kyc_general_status === "ProcessingFailedAndSentToManager" ? (
                                        <span className={st.danger}>
                                            {t("failed_kyc1")}
                                        </span>
                                    ) : null}
                                </>
                            )}
                            
                            {/*{(user?.kyc_general_status === "ProcessingSuccess" && status === "sign_with_kyc") && (*/}
                            {/*    <span className={st.sm_text}>{t("wait_to_update_loan")}</span>*/}
                            {/*)}*/}
                            
                            {/*{(status === "sign_with_kyc" && !user?.permissions.is_kyc_allowed && user?.kyc_general_status !== "ProcessingSuccess") && (*/}
                            {/*    <span className={st.sm_text}>{t("can_not_go_to_kyc")}</span>*/}
                            {/*)}*/}

                            {(contract_enable === false && simplified_status === "sign" && application.is_enable_to_sign) && (
                                <span className={clsx(st.sm_text, st.pending)}>
                                    {contract_error ? t("contract_err") : t("contract_is_pending")}
                                </span>
                            )}
                        </div>
                        
                        {((simplified_status === "approved_not_disbursed" || simplified_status === "disbursed" || simplified_status === "approved")
                            && user?.kyc_general_status === "ProcessingSuccess" && !!user?.identity_card_is_expiring_soon) ? (
                            <div className={st.contract}>
                                <Button
                                    className={st.dwn_contract}
                                    outlined
                                    onClick={() => {
                                        eventEmitter.emit(CommonClass.ModalEvents.OpenGetContractModal, application)
                                    }}
                                >
                                    {t("download_contract")}
                                    <DownloadIcon />
                                </Button>
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
        </>
    )
}

export default Application